package com.htmlagent.site.api

import arrow.core.Either
import com.htmlagent.shared.UiLocale
import com.htmlagent.shared.session.ArtifactHistoryResponse
import com.htmlagent.shared.session.ImageUpload
import com.htmlagent.shared.session.SessionDetailResponse
import com.htmlagent.shared.session.SessionListResponse
import com.htmlagent.shared.session.StartSessionResponse
import com.htmlagent.shared.stream.StreamEvent

/**
 * What the screen needs from the API, as a contract rather than as a class.
 *
 * It exists so the chat's state can be tested without a browser and without a server: the only
 * other implementation is the fake in `jsTest`, and it is the second one from the day this was
 * extracted — the reason for extracting it was a button that did nothing on a reopened session
 * and had no test that could have caught it.
 *
 * Every call answers with [Either] and none of them throws: a failure is a value the screen has
 * to handle, and the compiler says so. Signing in is not here: the screen's state never does
 * it, the sign-in pages do, on [AgentClient] itself.
 */
interface AgentApi {

    suspend fun startSession(): Either<ApiFailure, StartSessionResponse>

    suspend fun sessions(): Either<ApiFailure, SessionListResponse>

    suspend fun deleteSession(sessionId: String): Either<ApiFailure, Unit>

    suspend fun session(sessionId: String): Either<ApiFailure, SessionDetailResponse>


    suspend fun sendMessage(
        sessionId: String,
        prompt: String,
        locale: UiLocale,
        images: List<ImageUpload> = emptyList(),
    ): Either<ApiFailure, Unit>

    suspend fun cancel(sessionId: String): Either<ApiFailure, Unit>

    /**
     * Takes the conversation back to just before one of the person's messages, pages included.
     * With [resend], its new text then goes out as a fresh turn: that is what editing is.
     */
    suspend fun rewind(sessionId: String, messageId: Long, resend: Resend? = null): Either<ApiFailure, Unit>

    fun openStream(
        sessionId: String,
        onEvent: (StreamEvent) -> Unit,
        onLost: () -> Unit,
        liveOnly: Boolean = false,
    ): AgentStream


    suspend fun versionCode(sessionId: String, version: Int): Either<ApiFailure, String>

    suspend fun versions(sessionId: String): Either<ApiFailure, ArtifactHistoryResponse>
}

/**
 * A stream that has been opened and can be closed.
 *
 * The contract says "closeable" and not `EventSource`, which is the browser's: with the browser
 * type in the signature, anything standing in for the API would have to produce a real one.
 */
fun interface AgentStream {
    fun close()
}

/**
 * The new text of a rewound message, the language to answer it in, and which of its photos to
 * keep by position.
 */
data class Resend(val text: String, val locale: UiLocale, val keptImages: List<Int>? = null)
